import { SideModal } from "shared";
import SVG from "shared/SVG";
import { SVG_ENUM } from "enums";
import "./LoanDetailsSideModal.scss";

interface LoanDetailsSideModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
}

const INTEREST_TYPE_LABELS: Record<string, string> = {
  fixed: "Stałe",
  variable: "Zmienne",
  periodically_fixed: "Okresowo-stałe",
};

const LOAN_TYPE_LABELS: Record<string, string> = {
  borrowed: "Zaciągnięta",
  lent: "Udzielona",
};

const PAYMENT_LABELS: Record<string, string> = {
  monthly: "Miesięczne",
  quarterly: "Kwartalne",
  yearly: "Roczne",
  at_end_of_year: "Na koniec roku",
};

const LoanDetailsSideModal = ({
  isOpen,
  onClose,
  item,
}: LoanDetailsSideModalProps) => {
  const formatDate = (dateString: any): string => {
    if (!dateString) return "Brak";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Brak";
    return date.toLocaleDateString("pl-PL");
  };

  const getDurationText = (): string => {
    if (!item) return "Brak";
    const durationDate = item.details?.durationDate;
    if (durationDate === null || durationDate === undefined)
      return "Bezterminowy";
    if (typeof durationDate === "string" && durationDate !== "") {
      const d = new Date(durationDate);
      if (isNaN(d.getTime())) return "Brak";
      if (d.getFullYear() === 2000 && d.getMonth() === 0 && d.getDate() === 1) {
        return "Bezterminowy";
      }
      return d.toLocaleDateString("pl-PL");
    }
    return "Brak";
  };

  const contractFiles = (item?.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const handleDownloadAll = () => {
    if (!item) return;
    contractFiles.forEach((file: any) => {
      const link = document.createElement("a");
      link.href = file.file.url;
      link.download = file.file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const primaryValue = item?.primaryValue;
  const currency = item?.currency?.currency;
  const formattedAmount =
    primaryValue !== null && primaryValue !== undefined
      ? `${Number(primaryValue).toLocaleString("pl-PL", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}${currency ? ` ${currency}` : ""}`
      : "Brak";

  const loanType = item?.loanType ?? item?.details?.loanType;
  const interestRate = item?.details?.interestRate;
  const interestRateType =
    item?.details?.interestRateType ?? item?.details?.interestType;
  const interestPayment = item?.details?.interestPayment;
  const autoRenewal = item?.details?.autoRenewal;

  return (
    <SideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Szczegółowe informacje"
      subtitle="Aktywo: Pożyczki"
      className="side-modal--loan-details"
    >
      <div className="loan-details">
        <div className="loan-details__price-header">
          Kwota: <span>{formattedAmount}</span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Podmiot</span>
          <span className="loan-details__value">
            {(item?.entity ?? item?.details?.entity) || "Brak"}
          </span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Rodzaj</span>
          <span className="loan-details__value">
            {loanType ? LOAN_TYPE_LABELS[loanType] ?? loanType : "Brak"}
          </span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Wysokość oprocentowania</span>
          <span className="loan-details__value">
            {interestRate !== null &&
            interestRate !== undefined &&
            interestRate !== ""
              ? `${interestRate}%`
              : "Brak"}
          </span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Rodzaj oprocentowania</span>
          <span className="loan-details__value">
            {interestRateType
              ? INTEREST_TYPE_LABELS[interestRateType] ?? interestRateType
              : "Brak"}
          </span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Odsetki</span>
          <span className="loan-details__value">
            {interestPayment
              ? PAYMENT_LABELS[interestPayment] ?? interestPayment
              : "Brak"}
          </span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Data zawarcia</span>
          <span className="loan-details__value">
            {formatDate(item?.details?.contractDate)}
          </span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Czas trwania</span>
          <span className="loan-details__value">{getDurationText()}</span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Automatyczne przedłużenie</span>
          <span className="loan-details__value">
            {autoRenewal === true || autoRenewal === 1
              ? "Tak"
              : autoRenewal === false || autoRenewal === 0
              ? "Nie"
              : "Brak"}
          </span>
        </div>

        <div className="loan-details__field">
          <span className="loan-details__label">Dodatkowe informacje</span>
          <span className="loan-details__value">
            {item?.additionalInfo || "Brak"}
          </span>
        </div>

        <div className="loan-details__field loan-details__field--files">
          <div className="loan-details__files-header">
            <span className="loan-details__label">Umowa</span>
            {contractFiles.length > 0 && (
              <button
                className="loan-details__download-all"
                onClick={handleDownloadAll}
              >
                Pobierz wszystkie
                <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
              </button>
            )}
          </div>
          {contractFiles.length === 0 ? (
            <span className="loan-details__value">Brak</span>
          ) : (
            <div className="loan-details__files-list">
              {contractFiles.map((file: any) => (
                <div key={file.file.uuid} className="loan-details__file-item">
                  <span className="loan-details__file-name">
                    {file.file.name}
                  </span>
                  <a
                    href={file.file.url}
                    download={file.file.name}
                    target="_blank"
                    rel="noreferrer"
                    className="loan-details__file-download"
                  >
                    <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default LoanDetailsSideModal;
